import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../components/Card";
import Button from "../components/Button";
import Input from "../components/Input";
import {
  adminListUsers,
  adminCreateUser,
  listWorksites,
} from "../services/api";
import { loadSession } from "../auth";
import { useAdminScope } from "../hooks/useAdminScope";
import { useTranslation } from "react-i18next";

function RoleBadge({ roles = [] }) {
  const { t } = useTranslation();
  const r = Array.isArray(roles) ? roles : [];
  if (!r.length) return <span className="text-xs text-slate-400">{t("admin_users.no_roles")}</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {r.map((role) => (
        <span
          key={role}
          className="px-2 py-0.5 rounded-full text-xs border bg-slate-100 border-slate-200 text-slate-600"
        >
          {role}
        </span>
      ))}
    </div>
  );
}

export default function AdminUsers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const session = loadSession();
  const me = session?.user;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [onlyManagers, setOnlyManagers] = useState(""); // "", "true", "false"
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  // Worksite filter (tenant-wide admins only)
  const [worksites, setWorksites] = useState([]);
  const { worksiteId, setWorksiteId } = useAdminScope(me?.id); // "" = all

  const isOrgAdmin =
    me?.derived?.isOrgAdmin ||
    (Array.isArray(me?.roles) && me.roles.includes("org_super_admin"));
  const isPlatformAdmin = me?.derived?.isPlatformAdmin || (Array.isArray(me?.roles) && me.roles.includes("platform_admin"));

  // Determine scope of this admin
  // worksite_id in user payload: if set → worksite-scoped; if null → tenant-wide
  const myWorksiteId = me?.worksite_id ?? null; // null = tenant-wide
  const isWorksiteScoped = isOrgAdmin && myWorksiteId !== null;

  // Load worksites for the filter dropdown (tenant-wide admins only)
  useEffect(() => {
    if (!isOrgAdmin || isWorksiteScoped) return;
    listWorksites()
      .then((data) => setWorksites(data.items || []))
      .catch(() => setWorksites([]));
  }, [isOrgAdmin, isWorksiteScoped]);

  // Validate saved worksite against the loaded list
  useEffect(() => {
    if (!worksiteId || worksites.length === 0) return;
    const ids = worksites.map((w) => String(w.id ?? ""));
    if (!ids.includes(String(worksiteId))) setWorksiteId("");
  }, [worksites, worksiteId, setWorksiteId]);

  // Create form (new users only)
  const emptyForm = { name: "", email: "", password: "", isManager: false };
  const [form, setForm] = useState(emptyForm);
  const [formVisible, setFormVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const params = {
        q,
        page,
        pageSize,
        isManager: onlyManagers,
      };
      // For worksite-scoped admin: pass their worksite_id; backend also enforces this
      if (isWorksiteScoped) {
        params.worksiteId = String(myWorksiteId);
      } else if (worksiteId !== "") {
        params.worksiteId = worksiteId;
      }
      const { items } = await adminListUsers(params);
      setRows(items || []);
    } catch (e) {
      setErr(e?.message || t("admin_users.error_generic"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, page, onlyManagers, worksiteId, isWorksiteScoped, myWorksiteId]);

  const filtered = useMemo(() => rows, [rows]);

  // ---- Create user flow ----
  const startCreate = () => {
    setForm(emptyForm);
    setFormVisible(true);
  };

  const resetForm = () => {
    setForm(emptyForm);
    setFormVisible(false);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setSubmitting(true);
    try {
      await adminCreateUser({
        name: form.name,
        email: form.email,
        password: form.password || undefined,
        isManager: form.isManager,
      });
      await load();
      resetForm();
    } catch (e2) {
      alert(e2?.message || t("admin_users.save_failed"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOrgAdmin && !isPlatformAdmin) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-6">
        <Card>
          <div className="text-sm text-slate-600">{t("admin_users.need_role")}</div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("admin_users.title")}</h1>
          {isWorksiteScoped && me?.worksite?.name && (
            <div className="text-sm text-slate-500 mt-0.5">
              {t("admin_users.worksite_filter_label")}: <span className="font-medium text-slate-700">{me.worksite.name}</span>
            </div>
          )}
        </div>
        <Button onClick={startCreate}>{t("admin_users.btn_new_user")}</Button>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2">
        <input
          className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white text-slate-900 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          placeholder={t("admin_users.search_placeholder")}
          value={q}
          onChange={(e) => { setPage(1); setQ(e.target.value); }}
        />
        <select
          className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          value={onlyManagers}
          onChange={(e) => { setPage(1); setOnlyManagers(e.target.value); }}
        >
          <option value="">{t("admin_users.filter_all")}</option>
          <option value="true">{t("admin_users.filter_managers")}</option>
          <option value="false">{t("admin_users.filter_non_managers")}</option>
        </select>
        {!isWorksiteScoped && (
          <select
            className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            value={worksiteId}
            onChange={(e) => { setPage(1); setWorksiteId(e.target.value); }}
          >
            <option value="">{t("admin_users.worksite_filter_all")}</option>
            {worksites.map((ws) => (
              <option key={ws.id} value={String(ws.id)}>{ws.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Create form (new users) */}
      {formVisible && (
        <Card title={t("admin_users.form_create_title")}>
          <form className="grid sm:grid-cols-2 gap-3" onSubmit={submit}>
            <div className="sm:col-span-1">
              <label className="block text-xs text-slate-500 font-medium mb-1">{t("admin_users.label_name")}</label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder={t("admin_users.ph_name")}
              />
            </div>
            <div className="sm:col-span-1">
              <label className="block text-xs text-slate-500 font-medium mb-1">{t("admin_users.label_email")}</label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder={t("admin_users.ph_email")}
              />
            </div>
            <div className="sm:col-span-1">
              <label className="block text-xs text-slate-500 font-medium mb-1">{t("admin_users.label_password")}</label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                placeholder={t("admin_users.ph_password")}
              />
            </div>
            <div className="sm:col-span-1 flex items-center gap-2 mt-6">
              <input
                type="checkbox"
                id="isManager"
                checked={form.isManager}
                onChange={(e) => setForm((f) => ({ ...f, isManager: e.target.checked }))}
              />
              <label htmlFor="isManager" className="text-sm text-slate-700">{t("admin_users.label_manager")}</label>
            </div>
            <div className="sm:col-span-2 flex gap-2 mt-2">
              <Button type="submit" disabled={submitting}>
                {submitting ? t("admin_users.btn_saving") : t("admin_users.btn_save")}
              </Button>
              <Button type="button" variant="ghost" onClick={resetForm} disabled={submitting}>
                {t("admin_users.btn_cancel")}
              </Button>
            </div>
          </form>
        </Card>
      )}

      <Card noPadding>
        {loading ? (
          <div className="p-6 text-sm text-slate-500">{t("admin_users.loading")}</div>
        ) : err ? (
          <div className="p-6 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl">{err}</div>
        ) : filtered.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left border-b border-slate-200">
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{t("admin_users.table_name")}</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{t("admin_users.table_email")}</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{t("admin_users.table_is_manager")}</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{t("admin_users.table_manager_name")}</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{t("admin_users.table_roles")}</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">{t("admin_users.table_actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-slate-900">{u.name || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{u.email}</td>
                    <td className="px-4 py-3 text-slate-600">{u.derived?.isManager ? t("admin_users.yes") : t("admin_users.no")}</td>
                    <td className="px-4 py-3 text-slate-600">{u?.derived?.managerName || u?.manager?.name || u?.managerName || "—"}</td>
                    <td className="px-4 py-3"><RoleBadge roles={u.roles} /></td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" onClick={() => navigate(`/admin/users/${u.id}`)}>
                        {t("admin_users.btn_view_details")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-12 text-center">
            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3 text-slate-400 text-lg">👥</div>
            <p className="text-sm font-medium text-slate-700">{t("admin_users.no_users")}</p>
          </div>
        )}
      </Card>
    </div>
  );
}
